import React from "react"
import PropTypes from "prop-types"
import { formatCurrency, formatNumber, formatFechaHora, TIPO_COMPROBANTE } from "../helpers"

const InfoCard = ({ titulo, icon, accent = "#3b82f6", children }) => (
  <div style={{
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
    padding: 14, flex: "1 1 280px", minWidth: 0,
  }}>
    <div style={{
      display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
      paddingBottom: 8, borderBottom: "1px solid #f1f5f9",
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8,
        background: `${accent}1a`, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14,
      }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {titulo}
      </span>
    </div>
    {children}
  </div>
)

InfoCard.propTypes = { titulo: PropTypes.string, icon: PropTypes.string, accent: PropTypes.string, children: PropTypes.node }

const InfoRow = ({ label, value, mono = false }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13, gap: 12 }}>
    <span style={{ color: "#64748b", flexShrink: 0 }}>{label}</span>
    <span style={{
      color: "#0f172a", fontWeight: 600, textAlign: "right",
      fontFamily: mono ? "ui-monospace, SFMono-Regular, monospace" : undefined,
      wordBreak: "break-word",
    }}>
      {value ?? "—"}
    </span>
  </div>
)

InfoRow.propTypes = { label: PropTypes.string, value: PropTypes.node, mono: PropTypes.bool }

const ResumenTab = ({ data }) => {
  const cabecera = data?.cabecera || {}
  const totales = data?.totales || {}
  const emisor = cabecera.emisor || {}
  const receptor = cabecera.receptor || {}
  const trazabilidad = cabecera.trazabilidad || {}
  const cabTotales = cabecera.totales || {}

  const tipoLabel = TIPO_COMPROBANTE[cabecera.tipocomprobante] || cabecera.descripcion_tipo || cabecera.tipocomprobante

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Cards de info */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <InfoCard titulo="Identificación" icon="mdi-file-document-outline" accent="#3b82f6">
          <InfoRow label="Tipo" value={`${cabecera.tipocomprobante || "—"} · ${tipoLabel || "—"}`} />
          <InfoRow label="Prefijo · Folio" value={`${cabecera.prefijo || ""}-${cabecera.folio || ""}`} mono />
          <InfoRow label="Fecha emisión" value={formatFechaHora(cabecera.fecha)} />
          <InfoRow label="Fecha transmisión" value={formatFechaHora(cabecera.fechageneracion)} />
          <InfoRow label="Resolución" value={cabecera.resolucion} mono />
        </InfoCard>

        <InfoCard titulo="Emisor" icon="mdi-domain" accent="#6366f1">
          <InfoRow label="NIT" value={emisor.nit} mono />
          <InfoRow label="Razón social" value={emisor.nombre} />
          <InfoRow label="Sucursal" value={emisor.sucursal} />
          {(emisor.brandid || emisor.storeid) && (
            <InfoRow
              label="Brand · Store"
              value={`${emisor.brandid || "—"} · ${emisor.storeid || "—"}`}
              mono
            />
          )}
        </InfoCard>

        <InfoCard titulo="Receptor" icon="mdi-account" accent="#10b981">
          <InfoRow label="NIT" value={receptor.nit} mono />
          <InfoRow label="Nombre" value={receptor.nombre} />
        </InfoCard>

        <InfoCard titulo="Totales (cabecera)" icon="mdi-cash-multiple" accent="#f59e0b">
          <InfoRow label="Subtotal" value={formatCurrency(cabTotales.subtotal)} />
          <InfoRow label="Impuestos" value={formatCurrency(cabTotales.impuesto_total)} />
          {cabTotales.cargo_propina > 0 && (
            <InfoRow label="Cargo / propina" value={formatCurrency(cabTotales.cargo_propina)} />
          )}
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
            <InfoRow label={<strong>Total</strong>} value={
              <strong style={{ color: "#0f172a", fontSize: 16 }}>{formatCurrency(cabTotales.total)}</strong>
            } />
          </div>
        </InfoCard>

        {(trazabilidad.foliopost || trazabilidad.checkid || trazabilidad.identificadorordr) && (
          <InfoCard titulo="Trazabilidad POS" icon="mdi-link-variant" accent="#8b5cf6">
            <InfoRow label="Folio POS" value={trazabilidad.foliopost} mono />
            <InfoRow label="Check ID" value={trazabilidad.checkid} mono />
            <InfoRow label="ID orden" value={trazabilidad.identificadorordr} mono />
          </InfoCard>
        )}

        {/* Reconciliación líneas vs métodos */}
        <InfoCard titulo="Reconciliación" icon="mdi-scale-balance" accent="#06b6d4">
          <InfoRow label="Líneas (count)" value={formatNumber(totales.lineas_count)} />
          <InfoRow label="Subtotal líneas" value={formatCurrency(totales.lineas_subtotal)} />
          <InfoRow label="Impuestos líneas" value={formatCurrency(totales.lineas_impuestos)} />
          <InfoRow label="Total líneas" value={formatCurrency(totales.lineas_total)} />
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
            <InfoRow label="Métodos (original)" value={formatCurrency(totales.metodos_total_original)} />
            <InfoRow label="Métodos (ajustado)" value={formatCurrency(totales.metodos_total_ajustado)} />
          </div>
        </InfoCard>
      </div>
    </div>
  )
}

ResumenTab.propTypes = { data: PropTypes.object }

export default ResumenTab
